import { getSetting } from '@disy/workbook.lib/settings';

import type { SelectionLayerData } from 'cadenza/map/interactions/workbook-selection/workbook-map-selection-definitions';
import { getStore } from 'cadenza/store/cadenza-store';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { isDataViewLayer } from 'cadenza/workbook/map/workbook-layer-type-util';
import { getSortedLayerIds } from 'cadenza/workbook/map/workbook-layer-utils';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key.gen';
import { isDataViewRenderedOnClient, isLayerVisible } from 'cadenza/workbook/state/workbook-map-layer.state';
import {
  getDataViewIdsWithAllowedSelection,
  getLayerData,
  getViewById,
} from 'cadenza/workbook/workbook-core-selectors.state';

/**
 * Get all the selectable layers from the store:
 * - client-side and server-side data layers
 * - external layers with feature info
 */
export function getSelectableLayers(viewId: ViewId, selectableLayerIds?: LayerId[]): SelectionLayerData[] {
  // layers are sorted according to their position in the layer tree
  const layerIds = getSortedLayerIds(viewId);
  return (
    layerIds
      // If selectable layers were provided, keep only the specified layers
      .filter((layerId) => (selectableLayerIds ? selectableLayerIds.includes(layerId) : true))
      // exclude all non-visible layers
      .filter((layerId) => isLayerVisible(getStore().getState(), viewId, layerId))
      // exclude all layers that have selection disabled by configuration
      .filter((layerId) => isSelectableLayer(viewId, layerId))
      // get the layer data from the store
      .map((layerId) => getLayerData(getStore().getState(), viewId, layerId))
      // check if selectable and classify between server-side and client-side
      .map((layerData) => {
        const layer = layerData.definition;
        const dataViewId = getSetting<DataViewId>(layer, LayerSettingsKey.toPersistenceKey('DATA_VIEW_ID'));
        const isDataViewLayer = Boolean(dataViewId);

        const isExternalLayerWithFeatureInfo = getSetting<boolean>(
          layer,
          LayerSettingsKey.toPersistenceKey('EXTERNAL_LAYER_FEATURE_INFO'),
        );

        let layerWithSelectionType: SelectionLayerData | undefined;

        if (isDataViewLayer) {
          const isClientSideRendered = isDataViewRenderedOnClient(getStore().getState(), dataViewId!);
          if (isClientSideRendered) {
            layerWithSelectionType = { ...layerData, selectionType: 'client' };
          } else {
            layerWithSelectionType = { ...layerData, selectionType: 'server', serverSelectionType: 'dataLayer' };
          }
        } else if (isExternalLayerWithFeatureInfo) {
          layerWithSelectionType = { ...layerData, selectionType: 'server', serverSelectionType: 'external' };
        }
        return layerWithSelectionType;
      })
      .filter((layerOrUndefined) => Boolean(layerOrUndefined)) as SelectionLayerData[]
  );
}

function isSelectableLayer(viewId: ViewId, layerId: LayerId) {
  const layer = getViewById(getStore().getState(), viewId).layerDefinitions[layerId];
  return isDataViewLayer(layer) ?
      getDataViewIdsWithAllowedSelection(getStore().getState()).includes(layerId as DataViewId)
      // For external layers, there is no such setting to disable selection, so return always true
    : true;
}
